import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CreditCard, ExternalLink, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { PlanBadge, UpgradeButton, webOnlyNote } from "@/components/Locked";
import { isAndroidApp } from "@/lib/platform";
import { Hint } from "@/components/Hint";

const STATUS = {
  active: ["Active", "text-emerald-300"], trialing: ["Trial", "text-cyan-300"], past_due: ["Payment past due", "text-amber-300"],
  unpaid: ["Unpaid", "text-red-300"], canceled: ["Canceled", "text-slate-400"], incomplete: ["Incomplete", "text-amber-300"],
  incomplete_expired: ["Expired", "text-slate-400"], paused: ["Paused", "text-slate-400"], free: ["Free plan", "text-slate-300"],
};

export const BillingSection = () => {
  const { billing, plan, refresh, portal, checkout, mode } = useBilling();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState(null);

  useEffect(() => {
    if (params.get("billing") === "refresh") { refresh({ sync: true }); params.delete("billing"); setParams(params, { replace: true }); }
  }, [params, refresh, setParams]);
  useEffect(() => { if (billing?.has_customer) api.get("/billing/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([])); }, [billing?.has_customer]);

  if (!billing) return <div className="apx-card p-5 skeleton h-40" />;
  const sub = billing.subscription;
  const [statusLabel, statusCls] = STATUS[billing.status] || [billing.status, "text-slate-300"];
  const endIso = sub?.cancel_at_period_end ? (sub.cancel_at || sub.current_period_end) : sub?.current_period_end;
  const renew = endIso ? new Date(endIso).toLocaleDateString(undefined, { dateStyle: "medium" }) : null;
  const go = async (fn) => { setBusy(true); await fn(); setBusy(false); };

  return (
    <div className="apx-card p-5 space-y-4" data-testid="billing-card">
      <div className="flex items-center gap-2 flex-wrap">
        <CreditCard className="w-4 h-4 text-cyan-300" />
        <div className="font-display font-semibold">Subscription & Billing</div>
        <PlanBadge plan={plan} />
        <button data-testid="billing-refresh-btn" onClick={() => go(() => refresh({ sync: true }))} className="ml-auto btn-ghost rounded-lg px-2 py-1 text-xs inline-flex items-center gap-1" disabled={busy}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh</button>
      </div>

      {billing.status === "past_due" && (
        <div className="rounded-lg border border-amber-800 bg-amber-500/10 p-3 text-xs text-amber-200 flex gap-2" data-testid="billing-past-due">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {isAndroidApp() ? `Your last payment failed. It will be retried automatically; ${billing.plan_name} access continues once it succeeds.` : <>Your last payment failed. Stripe will retry automatically — update your card in the billing portal to keep {billing.plan_name} access.</>}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
        <Field label="Current plan" value={<span data-testid="billing-plan">{billing.plan_name}{isAndroidApp() ? "" : ` · $${billing.plans[plan].price.toFixed(2)}/mo`}</span>} />
        <Field label="Status" value={<span className={statusCls} data-testid="billing-status">{statusLabel}</span>} />
        <Field label={sub?.cancel_at_period_end ? "Access ends" : "Next renewal"} value={<span data-testid="billing-renewal">{renew || "—"}</span>} />
        <Field label="Billing" value={sub?.cancel_at_period_end ? <span className="text-amber-300" data-testid="billing-cancel-flag">Cancels at period end</span> : plan === "free" ? "Nothing to bill" : `${sub?.interval === "year" ? "Yearly" : "Monthly"}, auto-renews`} />
      </div>

      <div className="grid sm:grid-cols-3 gap-2 text-xs text-slate-400">
        {Object.entries(billing.usage || {}).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-800 bg-slate-900/50 p-2.5" data-testid={`usage-${k}`}>
            <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")} today</div>
            <div className="font-mono-ticker text-slate-200">{v.used} <span className="text-slate-500">/ {v.limit == null ? "unlimited" : v.limit}</span></div>
            {v.limit != null && <div className="h-1 mt-1.5 rounded bg-slate-800"><div className="h-1 rounded bg-cyan-400" style={{ width: `${Math.min(100, (v.used / v.limit) * 100)}%` }} /></div>}
          </div>
        ))}
      </div>

      {isAndroidApp() ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 text-xs text-slate-400" data-testid="billing-android-web-only">{webOnlyNote()}</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="billing-actions">
          {plan === "free" && <UpgradeButton plan="pro" size="md" testid="billing-upgrade-pro" />}
          {plan === "free" && <UpgradeButton plan="premium" size="md" testid="billing-upgrade-premium" />}
          {plan === "pro" && <button data-testid="billing-upgrade-premium" disabled={busy} onClick={() => go(() => checkout("apex_premium_monthly"))} className="rounded-lg px-4 py-2.5 text-sm font-semibold bg-amber-500/15 text-amber-200 border border-amber-800 hover:bg-amber-500/25">Upgrade to Premium</button>}
          {plan === "premium" && <button data-testid="billing-downgrade-pro" disabled={busy} onClick={() => go(() => checkout("apex_pro_monthly"))} className="rounded-lg px-4 py-2.5 text-sm font-semibold btn-ghost border border-slate-700">Switch to Pro</button>}
          {(billing.has_customer || plan !== "free") && (
            <Hint title="Stripe customer portal" text="Update your card, download invoices, switch plans or cancel. Hosted securely by Stripe." asChild>
              <button data-testid="billing-portal-btn" disabled={busy} onClick={() => go(portal)} className="rounded-lg px-4 py-2.5 text-sm font-semibold btn-ghost border border-slate-700 inline-flex items-center gap-2">Manage billing <ExternalLink className="w-3.5 h-3.5" /></button>
            </Hint>
          )}
          <Link to="/pricing" className="rounded-lg px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200" data-testid="billing-compare-plans">Compare plans</Link>
        </div>
      )}

      {invoices && invoices.length > 0 && (
        <div data-testid="billing-invoices">
          <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 mb-2">Billing history</div>
          <div className="divide-y divide-slate-900 border border-slate-800 rounded-lg overflow-hidden text-xs">
            {invoices.map((i) => (
              <div key={i.id} className="flex items-center gap-3 px-3 py-2 bg-slate-900/40">
                <span className="font-mono-ticker text-slate-300">{i.created ? new Date(i.created).toLocaleDateString() : "—"}</span>
                <span className="text-slate-500">{i.number || i.id}</span>
                <span className={`ml-auto capitalize ${i.status === "paid" ? "text-emerald-300" : i.status === "open" ? "text-amber-300" : "text-slate-400"}`}>{i.status}</span>
                <span className="font-mono-ticker">${(i.status === "paid" ? i.amount_paid : i.amount_due).toFixed(2)}</span>
                {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">View</a>}
              </div>
            ))}
          </div>
        </div>
      )}
      {mode === "test" && <div className="text-[11px] text-slate-500">Stripe test mode — use card 4242 4242 4242 4242 with any future expiry and CVC. No real charges are made.</div>}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div><div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{label}</div><div className="mt-0.5 font-medium">{value ?? "—"}</div></div>
);
